import React, { useEffect, useState, useRef } from 'react';
import { Loader2, Plus, Trash2, Upload, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { gmatApi } from '@/lib/gmatApi';
import { toast } from 'sonner';

const SECTIONS = ['quant', 'verbal', 'data_insights'];
const DIFFS = ['easy', 'medium', 'hard'];
const STATUSES = ['draft', 'reviewed', 'published'];

const blankQ = () => ({
  section: 'quant', topic: '', subtopic: '', difficulty: 'medium',
  question_text: '', passage_text: '', table_or_chart_data: '',
  option_a: '', option_b: '', option_c: '', option_d: '', option_e: '',
  correct_answer: 'a', full_explanation: '', trap_explanation: '',
  estimated_time_seconds: 120, source_type: 'winhouse_original', status: 'published',
});

const AdminGmatPanel = () => {
  const [tab, setTab] = useState('questions'); // 'questions' | 'students'
  const [list, setList] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSection, setFilterSection] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editing, setEditing] = useState(null); // null | new | existing question id
  const [draft, setDraft] = useState(blankQ());
  const fileRef = useRef(null);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filterSection) qs.set('section', filterSection);
      if (filterDifficulty) qs.set('difficulty', filterDifficulty);
      if (filterStatus) qs.set('status', filterStatus);
      const r = await gmatApi.adminListQuestions(qs.toString() ? `?${qs.toString()}` : '');
      setList(r.questions || []);
    } catch (e) {
      toast.error(e.message || 'Could not load questions');
    } finally { setLoading(false); }
  };

  const loadStudents = async () => {
    setLoading(true);
    try {
      const r = await gmatApi.adminStudents();
      setStudents(r.students || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === 'questions') loadQuestions();
    else loadStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filterSection, filterDifficulty, filterStatus]);

  const startNew = () => { setEditing('new'); setDraft(blankQ()); };
  const startEdit = (q) => { setEditing(q.id); setDraft({ ...blankQ(), ...q }); };

  const saveDraft = async () => {
    try {
      const payload = {
        ...draft,
        passage_text: draft.passage_text || null,
        table_or_chart_data: draft.table_or_chart_data || null,
        option_e: draft.option_e || null,
        subtopic: draft.subtopic || null,
        trap_explanation: draft.trap_explanation || null,
      };
      if (editing === 'new') await gmatApi.adminAddQuestion(payload);
      else await gmatApi.adminUpdateQuestion(editing, payload);
      toast.success('Saved');
      setEditing(null);
      loadQuestions();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try { await gmatApi.adminDeleteQuestion(id); toast.success('Deleted'); loadQuestions(); }
    catch (e) { toast.error(e.message); }
  };

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      let questions;
      if (file.name.endsWith('.json')) questions = JSON.parse(text);
      else throw new Error('Only .json bulk uploads supported in Phase 1');
      if (!Array.isArray(questions)) throw new Error('Expected an array of questions');
      const r = await gmatApi.adminBulkUpload(questions);
      toast.success(`Uploaded ${r.inserted} (skipped ${r.skipped})`);
      loadQuestions();
    } catch (e) { toast.error(e.message); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  const seedFromFile = async () => {
    if (!window.confirm('Seed the database with the bundled 150-question bank?\n\nThis is idempotent — questions with identical text are skipped.')) return;
    try {
      const r = await gmatApi.adminSeedFromFile();
      const c = r.current_db_counts || {};
      toast.success(`Seeded: ${r.inserted} new, ${r.duplicates_skipped} already present. Bank now: Quant ${c.quant || 0}, Verbal ${c.verbal || 0}, Data Insights ${c.data_insights || 0}.`);
      loadQuestions();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-gmat-panel">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold">GMAT Admin</h1>
          <div className="flex gap-2">
            <Button variant={tab === 'questions' ? 'default' : 'outline'} onClick={() => setTab('questions')} data-testid="agm-tab-questions">Questions</Button>
            <Button variant={tab === 'students' ? 'default' : 'outline'} onClick={() => setTab('students')} data-testid="agm-tab-students">Students</Button>
          </div>
        </div>

        {tab === 'questions' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle>Question Bank ({list.length})</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <select className="border rounded p-2 text-sm bg-white" value={filterSection} onChange={(e) => setFilterSection(e.target.value)} data-testid="agm-filter-section">
                    <option value="">All sections</option>
                    {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select className="border rounded p-2 text-sm bg-white" value={filterDifficulty} onChange={(e) => setFilterDifficulty(e.target.value)}>
                    <option value="">All difficulties</option>
                    {DIFFS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select className="border rounded p-2 text-sm bg-white" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">All statuses</option>
                    {STATUSES.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <Button onClick={startNew} className="gap-1.5" data-testid="agm-new"><Plus className="h-3.5 w-3.5" /> New question</Button>
                  <input type="file" accept=".json" ref={fileRef} onChange={onUpload} className="hidden" data-testid="agm-upload-input" />
                  <Button variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} data-testid="agm-upload"><Upload className="h-3.5 w-3.5" /> Upload JSON</Button>
                  <Button variant="outline" className="gap-1.5" onClick={seedFromFile} data-testid="agm-seed" title="Load the 150-question bundled question bank"><Database className="h-3.5 w-3.5" /> Seed bank (150)</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="agm-questions-table">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500"><tr>
                      <th className="px-3 py-2">Section</th><th className="px-3 py-2">Topic</th><th className="px-3 py-2">Diff</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Question</th><th className="px-3 py-2"></th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {list.map((q) => (
                        <tr key={q.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 capitalize">{q.section.replace('_', ' ')}</td>
                          <td className="px-3 py-2 capitalize">{(q.topic || '').replace(/_/g, ' ')}</td>
                          <td className="px-3 py-2 capitalize">{q.difficulty}</td>
                          <td className="px-3 py-2"><Badge className={q.status === 'published' ? 'bg-green-100 text-green-800 border-green-200 border' : 'bg-gray-100 text-gray-700 border border-gray-200'}>{q.status}</Badge></td>
                          <td className="px-3 py-2 max-w-md truncate text-gray-700" title={q.question_text}>{q.question_text}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <Button variant="outline" size="sm" onClick={() => startEdit(q)} className="mr-1">Edit</Button>
                            <Button variant="outline" size="sm" onClick={() => remove(q.id)} className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                          </td>
                        </tr>
                      ))}
                      {list.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-gray-500">No questions match the filters.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'students' && (
          <Card>
            <CardHeader><CardTitle>Students ({students.length})</CardTitle></CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
                <table className="w-full text-sm" data-testid="agm-students-table">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500"><tr>
                    <th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Attempts</th><th className="px-3 py-2">Accuracy</th><th className="px-3 py-2">Last mock</th><th className="px-3 py-2">Last login</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {students.map((s) => (
                      <tr key={s.user_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">{s.name || '—'}</td>
                        <td className="px-3 py-2">{s.email}</td>
                        <td className="px-3 py-2">{s.total_attempts}</td>
                        <td className="px-3 py-2">{s.accuracy}%</td>
                        <td className="px-3 py-2">{s.last_estimated_score ?? '—'}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">{s.last_login_at ? new Date(s.last_login_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                    {students.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-gray-500">No students yet.</td></tr>}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Editor modal */}
        {editing && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-auto" onClick={() => setEditing(null)}>
            <Card className="max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <CardHeader><CardTitle>{editing === 'new' ? 'New question' : 'Edit question'}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div><Label>Section</Label><select className="border rounded p-2 w-full" value={draft.section} onChange={(e) => setDraft({ ...draft, section: e.target.value })}>{SECTIONS.map((s) => <option key={s}>{s}</option>)}</select></div>
                  <div><Label>Difficulty</Label><select className="border rounded p-2 w-full" value={draft.difficulty} onChange={(e) => setDraft({ ...draft, difficulty: e.target.value })}>{DIFFS.map((d) => <option key={d}>{d}</option>)}</select></div>
                  <div><Label>Status</Label><select className="border rounded p-2 w-full" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>{STATUSES.map((d) => <option key={d}>{d}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Topic</Label><Input value={draft.topic} onChange={(e) => setDraft({ ...draft, topic: e.target.value })} /></div>
                  <div><Label>Subtopic</Label><Input value={draft.subtopic} onChange={(e) => setDraft({ ...draft, subtopic: e.target.value })} /></div>
                </div>
                <div><Label>Passage (optional)</Label><Textarea rows={2} value={draft.passage_text || ''} onChange={(e) => setDraft({ ...draft, passage_text: e.target.value })} /></div>
                <div><Label>Question</Label><Textarea rows={3} value={draft.question_text} onChange={(e) => setDraft({ ...draft, question_text: e.target.value })} data-testid="agm-edit-qtext" /></div>
                {['a', 'b', 'c', 'd', 'e'].map((k) => (
                  <div key={k}><Label>Option {k.toUpperCase()}{k === 'e' && ' (optional)'}</Label><Input value={draft[`option_${k}`] || ''} onChange={(e) => setDraft({ ...draft, [`option_${k}`]: e.target.value })} /></div>
                ))}
                <div><Label>Correct answer (a-e)</Label><select className="border rounded p-2 w-full" value={draft.correct_answer} onChange={(e) => setDraft({ ...draft, correct_answer: e.target.value })}>{['a', 'b', 'c', 'd', 'e'].map((k) => <option key={k}>{k}</option>)}</select></div>
                <div><Label>Full explanation</Label><Textarea rows={3} value={draft.full_explanation} onChange={(e) => setDraft({ ...draft, full_explanation: e.target.value })} /></div>
                <div><Label>Trap explanation (optional)</Label><Textarea rows={2} value={draft.trap_explanation || ''} onChange={(e) => setDraft({ ...draft, trap_explanation: e.target.value })} /></div>
                <div className="flex gap-2 justify-end pt-2"><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={saveDraft} className="bg-[#007A7A] text-white" data-testid="agm-edit-save">Save</Button></div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGmatPanel;
